<template>
    <v-dialog v-if="inspection" :value="value" @input="emitValue" max-width="800">
        <v-card class="pa-5">
            <div class="d-flex align-center font-weight-medium mb-4">
                <template v-if="slo">
                    Configure the "{{ inspection.title }}" inspection
                </template>
                <template v-else>
                    Adjust the threshold for the "{{ inspection.title }}" inspection
                </template>
                <v-spacer />
                <v-btn icon @click="emitValue(false)"><v-icon>mdi-close</v-icon></v-btn>
            </div>

            <v-form v-model="valid">
                <template v-if="slo">
                    Metrics:
                    <v-select v-model="slo_config.custom" :items="[{value: false, text: 'inbound requests (built-in)'}, {value: true, text: 'custom'}]" outlined dense :menu-props="{offsetY: true}" hide-details class="mb-3"/>
                    <div v-if="inspection.id === 'SLOAvailability'">
                        <template v-if="slo_config.custom">
                            Total requests query:
                            <MetricSelector :projectId="project_id" v-model="slo_config.total_requests_query" :rules="slo_config.custom ? [$validators.notEmpty] : []" wrap="sum( rate( <input> [..]) )" class="mb-3"/>

                            Failed requests query:
                            <MetricSelector :projectId="project_id" v-model="slo_config.failed_requests_query" :rules="slo_config.custom ? [$validators.notEmpty] : []" wrap="sum( rate( <input> [..]) )" class="mb-3"/>
                        </template>

                        Objective:
                        <div>
                            <v-text-field outlined dense v-model.number="slo_config.objective_percentage" :rules="[$validators.isFloat]" hide-details class="input">
                                <template #append><span class="grey--text">%</span></template>
                            </v-text-field>
                            of requests should not fail
                        </div>
                    </div>

                    <div v-if="inspection.id === 'SLOLatency'">
                        <template v-if="slo_config.custom">
                            Histogram query:
                            <MetricSelector :projectId="project_id" v-model="slo_config.histogram_query" :rules="[$validators.notEmpty]" wrap="sum by(le)( rate( <input> [..]) )" class="mb-3"/>
                        </template>

                        Objective:
                        <div>
                            <v-text-field outlined dense v-model.number="slo_config.objective_percentage" :rules="[$validators.notEmpty]" hide-details class="input text">
                                <template #append><span class="grey--text">%</span></template>
                            </v-text-field>
                            of requests should be served faster than
                            <v-select v-model.number="slo_config.objective_bucket" :items="latency_buckets" :rules="[$validators.notEmpty]" outlined dense hide-details :menu-props="{offsetY: true}" class="input select" />
                        </div>
                    </div>
                </template>

                <div v-else>
                    <v-simple-table>
                        <thead>
                        <tr>
                            <th>Level</th>
                            <th>Condition</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="application_id">
                            <td>Override for the <b>{{project_name ? project_name+'/' : ''}}{{$utils.appId(application_id).name}}</b> app</td>
                            <td>
                                <div v-if="application_override" class="d-flex align-center">
                                    <div class="flex-grow-1 capfirst py-3">
                                        {{condition.head}}
                                        <v-text-field outlined hide-details v-model.number="application_override.config.threshold" :rules="[$validators.isFloat]" class="input" />
                                        {{unit}} {{condition.tail}}
                                    </div>
                                    <v-btn small icon @click="restore(project_id, application_id)"><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
                                </div>
                                <div v-else class="grey--text">
                                    The project-level default &darr; is used. <a @click="override(project_id, application_id)">Override</a>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="project_id">
                            <td>
                                <template v-if="project_name">
                                    Override for the <b>{{project_name}}</b> project
                                </template>
                                <template v-else>
                                    Project-level override
                                </template>
                            </td>
                            <td>
                                <div v-if="project_override && project_override.config" class="d-flex align-center">
                                    <div class="flex-grow-1 capfirst py-3">
                                        {{condition.head}}
                                        <v-text-field outlined hide-details v-model.number="project_override.config.threshold" :rules="[$validators.isFloat]" class="input" />
                                        {{unit}} {{condition.tail}}
                                    </div>
                                    <v-btn small icon @click="restore(project_id)"><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
                                </div>
                                <div v-else class="grey--text">
                                    The workspace-level default &darr; is used. <a @click="override(project_id)">Override</a>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Workspace-level override</td>
                            <td>
                                <div v-if="workspace_override && workspace_override.config" class="d-flex align-center">
                                    <div class="flex-grow-1 capfirst py-3">
                                        {{condition.head}}
                                        <v-text-field outlined hide-details v-model.number="workspace_override.config.threshold" :rules="[$validators.isFloat]" class="input" />
                                        {{unit}} {{condition.tail}}
                                    </div>
                                    <v-btn small icon @click="restore()"><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
                                </div>
                                <div v-else class="grey--text">
                                    The global default &darr; is used. <a @click="override()">Override</a>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Global default</td>
                            <td>
                                <div class="d-flex align-center">
                                    <div class="flex-grow-1 capfirst py-3">
                                        {{condition.head}}
                                        <v-text-field outlined hide-details disabled :value="inspection.threshold" class="input" />
                                        {{unit}} {{condition.tail}}
                                    </div>
                                    <div style="min-width: 28px"></div>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </v-simple-table>
                </div>

                <v-alert v-if="error" color="red" icon="mdi-alert-octagon-outline" outlined text class="my-3">
                    {{error}}
                </v-alert>
                <v-alert v-if="message" color="green" outlined text class="my-3">
                    {{message}}
                </v-alert>
                <v-btn block color="primary" @click="save" :disabled="!(valid && changed)" :loading="loading" class="mt-5">Save</v-btn>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script>
import MetricSelector from "./MetricSelector.vue";

export default {
    props: {
        value: Boolean,
        inspection_id: String,
        project_id: String,
        project_name: String,
        application_id: String,
    },

    components: {MetricSelector},

    data() {
        return {
            loading: false,
            error: '',
            message: '',
            inspection: null,
            valid: false,
            saved: '',
        }
    },

    computed: {
        changed() {
            return this.saved !== JSON.stringify(this.inspection);
        },
        condition() {
            const parts = this.inspection.condition.split('<threshold>', 2);
            if (parts.length === 0) {
                return {head: '', tail: ''};
            }
            if (parts.length === 1) {
                return {head: parts[0], tail: ''};
            }
            return {head: parts[0], tail: parts[1]};
        },
        unit() {
            if (this.inspection.unit === 'second') {
                return 's';
            }
            if (this.inspection.unit === 'percent') {
                return '%';
            }
            return this.inspection.unit || '';
        },
        workspace_override() {
            return this.inspection.workspace_override;
        },
        project_override() {
            return (this.inspection.project_overrides || []).find(o => o.project_id === this.project_id);
        },
        application_override() {
            return (this.inspection.application_overrides || []).find(o => o.project_id === this.project_id && o.application_id === this.application_id);
        },
        slo() {
            return this.inspection.id.startsWith('SLO');
        },
        slo_config() {
            return this.application_override.config[0];
        },
        latency_buckets() {
            return [
                {value: 0.005, text: '5ms'},
                {value: 0.01, text: '10ms'},
                {value: 0.025, text: '25ms'},
                {value: 0.05, text: '50ms'},
                {value: 0.1, text: '100ms'},
                {value: 0.25, text: '250ms'},
                {value: 0.5, text: '500ms'},
                {value: 1, text: '1s'},
                {value: 2.5, text: '2.5s'},
                {value: 5, text: '5s'},
                {value: 10, text: '10s'},
            ];
        },
    },

    watch: {
        value() {
            if (this.value) {
                this.form = null;
                this.get();
            } else {
                this.error = '';
                this.message = '';
            }
        }
    },

    methods: {
        emitValue(v) {
            this.$emit('input', v);
        },
        override(project_id, application_id) {
            if (!project_id && !application_id) {
                this.inspection.workspace_override = {
                    config: {threshold: this.inspection.threshold},
                };
            }
            if (project_id && !application_id) {
                this.inspection.project_overrides.push({
                    project_id,
                    project_name: this.project_name,
                    config: {threshold: this.inspection.threshold},
                });
            }
            if (project_id && application_id) {
                this.inspection.application_overrides.push({
                    project_id,
                    project_name: this.project_name,
                    application_id,
                    config: {threshold: this.inspection.threshold},
                });
            }
        },
        restore(project_id, application_id) {
            if (!project_id && !application_id) {
                this.inspection.workspace_override.config = null;
            }
            if (project_id && !application_id) {
                this.inspection.project_overrides.splice(this.inspection.project_overrides.findIndex(o => o.project_id === project_id), 1);
            }
            if (project_id && application_id) {
                this.inspection.application_overrides.splice(this.inspection.application_overrides.findIndex(o => o.project_id === project_id && o.application_id === application_id), 1);
            }
        },
        get() {
            this.loading = true;
            this.$api.getInspections((data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = 'Failed to get inspection. Please try again later.'
                    return;
                }
                this.inspection = data.inspections.find(i => i.id === this.inspection_id);
                if (!this.inspection.project_overrides) {
                    this.inspection.project_overrides = [];
                }
                if (!this.inspection.application_overrides) {
                    this.inspection.application_overrides = [];
                }
                if (this.slo && !this.application_override) {
                    this.inspection.application_overrides.push({
                        project_id: this.project_id,
                        project_name: this.project_name,
                        application_id: this.application_id,
                        config: [{custom: false, objective_percentage: this.inspection.threshold, objective_bucket: 0.5}],
                    });
                }
                this.saved = JSON.stringify(this.inspection);
            });
        },
        save() {
            this.loading = true;
            this.error = '';
            this.message = '';
            const form = {
                inspection_id: this.inspection_id,
                workspace_override: this.workspace_override,
            };
            if (this.project_id) {
                form.project_override = {
                    project_id: this.project_id,
                    config: this.project_override ? this.project_override.config : null,
                };
            }
            if (this.project_id && this.application_id) {
                form.application_override = {
                    project_id: this.project_id,
                    application_id: this.application_id,
                    config: this.application_override ? this.application_override.config : null,
                };
            }
            this.$api.saveInspection(form,(data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = 'Failed to save inspection. Please try again later.'
                    return;
                }
                this.message = 'Settings were successfully updated.';
                setTimeout(() => {
                    this.emitValue(false);
                }, 1000);
            });
        },
    },
}
</script>

<style scoped>
.input {
    display: inline-flex;
    max-width: 8ch;
}
.input >>> .v-input__slot {
    min-height: initial !important;
    height: 1.5rem !important;
    padding: 0 8px !important;
}
.input >>> .v-input__append-inner {
    margin-top: 4px !important;
}
.input.select {
    max-width: 11ch;
}
.input.select >>> .v-input__append-inner {
    margin-top: 0 !important;
}
.input >>> .v-select__selection--comma {
    margin: 0 !important;
}
* >>> .v-list-item {
    min-height: 32px !important;
    padding: 0 8px !important;
}
</style>